import React, { useState } from 'react';
import FirebaseImageUpload from '../components/FirebaseImageUpload';
import { callApi } from '../api/api';

const UploadPrescriptionView = ({ showPage, isAuthenticated, currentUser }) => {
    const [testText, setTestText] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [landmark, setLandmark] = useState('');
    const [pincode, setPincode] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadedUrls, setUploadedUrls] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [isEditingDetails, setIsEditingDetails] = useState(false);
    const [referredBy, setReferredBy] = useState('');
    const [patientAge, setPatientAge] = useState('');
    const [patientGender, setPatientGender] = useState('');

    React.useEffect(() => {
        if (isAuthenticated && currentUser) {
            setName(currentUser.name || '');
            setPhone(currentUser.mobile || '');
            
            let defaultAddress = currentUser.address || '';
            if (currentUser.addresses && currentUser.addresses.length > 0) {
                const addr = currentUser.addresses[0];
                defaultAddress = `${addr.line1}, ${addr.line2 ? addr.line2 + ', ' : ''}${addr.city}, ${addr.state}`;
                if (addr.pincode) setPincode(addr.pincode);
                if (addr.landmark) setLandmark(addr.landmark);
            }
            setAddress(defaultAddress);

            if (!currentUser.name || !currentUser.mobile || !defaultAddress) {
                setIsEditingDetails(true);
            } else {
                setIsEditingDetails(false);
            }
        } else {
            setIsEditingDetails(true);
        }
    }, [isAuthenticated, currentUser]);

    const handleSaveAndSend = async () => {
        if (!isAuthenticated) {
            setValidationError('Please login first to submit a prescription.');
            return;
        }

        if (!name.trim() || !phone.trim() || !address.trim()) {
            setValidationError('Name, mobile number, and address are required.');
            return;
        }

        if (uploadedUrls.length === 0 && testText.trim() === '') {
            setValidationError('Please either upload a prescription or type the medicine names.');
            return;
        }

        setIsSubmitting(true);
        setValidationError('');
        
        try {
            const subject = encodeURIComponent(`New Prescription Upload from ${name}`);
            let bodyText = `Name: ${name}\nPhone: ${phone}\nAddress: ${address}\n`;
            if (landmark) bodyText += `Landmark: ${landmark}\n`;
            if (pincode) bodyText += `Pincode: ${pincode}\n`;
            if (patientAge) bodyText += `Patient Age: ${patientAge}\n`;
            if (patientGender) bodyText += `Patient Gender: ${patientGender}\n`;
            if (referredBy) bodyText += `Referred By: ${referredBy}\n`;
            bodyText += `\n`;

            if (uploadedUrls.length > 0) {
                bodyText += `Prescription File: ${uploadedUrls[0]}\n`;
            }

            if (testText.trim() !== '') {
                bodyText += `Typed Medicines: ${testText}\n`;
            }

            const body = encodeURIComponent(bodyText);
            
            // Open email client
            window.location.href = `mailto:orders@qwickmed.com?subject=${subject}&body=${body}`;

            setUploadSuccess(true);
            setTestText('');
            setUploadedUrls([]);
        } catch (error) {
            console.error('Error submitting prescription:', error);
            setValidationError('Failed to prepare email. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (uploadSuccess) {
        return (
            <section className="page bg-slate-50 p-4 md:p-8 rounded-lg flex items-center justify-center min-h-[60vh]">
                <div className="max-w-md mx-auto bg-white p-8 rounded-2xl shadow-sm border border-emerald-100 text-center">
                    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="fas fa-check text-4xl text-emerald-500"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Request Received!</h2>
                    <p className="text-slate-600 mb-6">
                        Thank you, {currentUser?.name || 'Customer'}. We have received your request and will process it shortly. Our team will contact you if we need any more details.
                    </p>
                    <button
                        onClick={() => {
                            setUploadSuccess(false);
                            showPage('home');
                        }}
                        className="bg-sky-500 hover:bg-sky-600 text-white font-bold py-3 px-8 rounded-xl transition-colors shadow-sm"
                    >
                        Go to Home
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section id="upload-prescription" className="page bg-slate-50 p-4 md:p-8 rounded-lg min-h-screen">
            <h2 className="text-3xl font-extrabold text-center text-sky-800 mb-2">Order via Prescription or List</h2>
            <p className="text-center text-slate-600 font-medium mb-8">Take a photo of a handwritten list, upload your prescription, or manually type custom products below.</p>
            
            <div className="max-w-4xl mx-auto bg-white p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 relative">
                {isSubmitting && (
                    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-20 flex flex-col items-center justify-center rounded-3xl">
                        <i className="fas fa-spinner fa-spin text-4xl text-sky-500 mb-4"></i>
                        <p className="font-bold text-sky-700">Submitting your order...</p>
                    </div>
                )}
                
                {validationError && (
                    <div className="mb-6 p-4 bg-red-50 text-red-600 text-sm rounded-xl font-semibold text-center border border-red-100">
                        {validationError}
                    </div>
                )}
                
                {!isEditingDetails && isAuthenticated && currentUser ? (
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 mb-8 relative z-10">
                        <div className="flex justify-between items-start mb-2">
                            <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2"><i className="fas fa-map-marker-alt text-sky-500"></i> Deliver To</h3>
                            <button type="button" onClick={() => setIsEditingDetails(true)} className="text-sky-600 text-sm font-bold hover:underline bg-white px-4 py-2 rounded-xl border border-sky-200 shadow-sm transition-colors hover:bg-sky-50 flex items-center gap-2"><i className="fas fa-edit"></i> Change Details</button>
                        </div>
                        <p className="font-bold text-slate-800 mt-4">{name} <span className="text-slate-500 font-normal ml-2">{phone}</span></p>
                        <p className="text-slate-600 mt-1">{address}</p>
                        {(landmark || pincode) && <p className="text-slate-500 text-sm mt-1">{landmark && `Landmark: ${landmark}`}{landmark && pincode && ', '} {pincode && `Pincode: ${pincode}`}</p>}
                    </div>
                ) : (
                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 mb-8 relative z-10">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2"><i className="fas fa-address-card text-sky-500"></i> Contact & Delivery Details</h3>
                            {isAuthenticated && currentUser && currentUser.name && currentUser.mobile && (
                                <button type="button" onClick={() => setIsEditingDetails(false)} className="text-slate-500 font-medium text-sm hover:text-slate-800 hover:bg-slate-200 bg-white border border-slate-300 px-3 py-1.5 rounded-lg transition-colors">Cancel Edit</button>
                            )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="flex flex-col">
                                <label className="text-sm font-extrabold text-sky-600 uppercase tracking-widest mb-2">Patient's Full Name *</label>
                                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your name" className="bg-white border border-slate-200 rounded-xl p-3 text-slate-800 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all" />
                            </div>
                            <div className="flex flex-col">
                                <label className="text-sm font-extrabold text-sky-600 uppercase tracking-widest mb-2">Phone Number *</label>
                                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Enter your mobile number" className="bg-white border border-slate-200 rounded-xl p-3 text-slate-800 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all" />
                            </div>
                        </div>

                        {/* Referred By + Age & Gender */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="flex flex-col">
                                <label className="text-sm font-extrabold text-violet-600 uppercase tracking-widest mb-2 flex items-center gap-1.5"><i className="fas fa-user-tag"></i> Referred By <span className="text-violet-400 font-normal normal-case tracking-normal">(optional)</span></label>
                                <input type="text" value={referredBy} onChange={(e) => setReferredBy(e.target.value)} placeholder="Doctor or person who referred" className="bg-white border border-violet-200 rounded-xl p-3 text-slate-800 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 transition-all" />
                            </div>
                            <div className="flex flex-col">
                                <label className="text-sm font-extrabold text-rose-600 uppercase tracking-widest mb-2 flex items-center gap-1.5"><i className="fas fa-user"></i> Patient Info <span className="text-rose-400 font-normal normal-case tracking-normal">(optional)</span></label>
                                <div className="flex gap-2">
                                    <input type="number" min="1" max="120" value={patientAge} onChange={(e) => setPatientAge(e.target.value)} placeholder="Age" className="w-24 bg-white border border-rose-200 rounded-xl p-3 text-slate-800 outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 transition-all font-bold text-center" />
                                    <select value={patientGender} onChange={(e) => setPatientGender(e.target.value)} className="flex-1 bg-white border border-rose-200 rounded-xl p-3 text-slate-800 outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 transition-all font-semibold cursor-pointer">
                                        <option value="">Gender</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="mb-4">
                            <div className="flex flex-col">
                                <label className="text-sm font-extrabold text-sky-600 uppercase tracking-widest mb-2">Delivery Address *</label>
                                <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Enter your delivery address" className="bg-white border border-slate-200 rounded-xl p-3 text-slate-800 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all w-full" />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col">
                                <label className="text-sm font-extrabold text-sky-600 uppercase tracking-widest mb-2">Landmark *</label>
                                <input type="text" value={landmark} onChange={(e) => setLandmark(e.target.value)} placeholder="e.g. Near Apollo Hospital" className="bg-white border border-slate-200 rounded-xl p-3 text-slate-800 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all" />
                            </div>
                            <div className="flex flex-col">
                                <label className="text-sm font-extrabold text-sky-600 uppercase tracking-widest mb-2">Pincode *</label>
                                <input type="text" value={pincode} onChange={(e) => setPincode(e.target.value)} placeholder="e.g. 400001" className="bg-white border border-slate-200 rounded-xl p-3 text-slate-800 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all" />
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 relative z-10">
                    
                    {/* Upload Option */}
                    <div className="flex flex-col">
                        <p className="text-sm font-extrabold text-sky-600 uppercase tracking-widest flex items-center gap-2 mb-3">
                            <i className="fas fa-cloud-upload-alt"></i> Upload Option
                        </p>
                        <div className="flex-1 bg-slate-50/50 rounded-xl p-4 border border-slate-200">
                            <FirebaseImageUpload 
                                onUploadSuccess={(urls) => setUploadedUrls(Array.isArray(urls) ? urls : [urls])} 
                                folder="prescriptions"
                                allowMultiple={true}
                                maxFiles={3}
                            />
                        </div>
                    </div>

                    {/* Text Option */}
                    <div className="flex flex-col">
                        <p className="text-sm font-extrabold text-sky-600 uppercase tracking-widest flex items-center gap-2 mb-3">
                            <i className="fas fa-keyboard"></i> Or Type Medicines List
                        </p>
                        <textarea 
                            value={testText}
                            onChange={(e) => setTestText(e.target.value)}
                            className="flex-1 w-full bg-slate-50/50 border border-slate-200 rounded-xl p-4 text-slate-800 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 transition-all placeholder-slate-400 resize-none min-h-[150px]"
                            placeholder="e.g. Dolo 650, Digene..."
                        ></textarea>
                    </div>
                </div>

                <div className="flex gap-4 relative z-10 flex-col sm:flex-row">
                    <button 
                        onClick={handleSaveAndSend}
                        disabled={isSubmitting}
                        className="flex-1 bg-sky-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:bg-sky-700 hover:shadow-xl transition-all flex items-center justify-center gap-2 text-lg disabled:opacity-50"
                    >
                        <i className={`fas ${isSubmitting ? 'fa-spinner fa-spin' : 'fa-paper-plane'} text-xl`}></i> {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                    <button 
                        onClick={() => window.open('https://wa.me/919029122955?text=Hi,%20I%20want%20to%20order%20medicines', '_blank')}
                        className="flex-1 bg-[#25D366] text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:bg-[#128C7E] hover:shadow-xl transition-all flex items-center justify-center gap-2 text-lg"
                    >
                        <i className="fab fa-whatsapp text-2xl"></i> WhatsApp Us
                    </button>
                    <button
                        onClick={() => window.location.href = 'tel:+919029122955'}
                        className="flex-1 bg-white border-2 border-slate-200 hover:border-sky-500 hover:bg-sky-50 text-slate-700 font-bold py-4 px-6 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 text-lg"
                    >
                        <i className="fas fa-phone-alt text-sky-500"></i> Call Us
                    </button>
                </div>
            </div>
        </section>
    );
};

export default UploadPrescriptionView;
